import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import Seo from "@/components/Seo";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-20" data-testid="reset-page">
      <Seo title="Set a new password | Appleman Tech Repair" description="Set a new password for your Appleman Tech Repair account." />
      <p className="eyebrow text-accent mb-4">Account help</p>
      <h1 className="font-display font-black uppercase tracking-tighter text-4xl mb-8">Choose a new password</h1>
      {done ? (
        <div className="border-2 border-black shadow-hard p-8 text-center" data-testid="reset-done">
          <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-accent" />
          <p className="text-neutral-600">Your password is updated.</p>
          <Link to="/login" data-testid="reset-login-link"
            className="inline-block mt-6 border-2 border-black bg-black text-white px-6 py-3 font-display font-bold uppercase tracking-wider shadow-hard-sm brutal-hover">
            Log in
          </Link>
        </div>
      ) : !token ? (
        <div className="border-2 border-black shadow-hard p-8" data-testid="reset-no-token">
          <p className="text-neutral-600">This reset link is missing its token. Ask for a new one:</p>
          <Link to="/forgot-password" className="inline-block mt-4 font-display font-bold uppercase tracking-wider border-b-2 border-black" data-testid="reset-request-link">
            Request a reset link
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="border-2 border-black shadow-hard p-8 space-y-5" data-testid="reset-form">
          <div>
            <label className="eyebrow block mb-2" htmlFor="reset-password">New password</label>
            <input id="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} data-testid="reset-password-input"
              className="w-full border-2 border-black px-4 py-3 outline-none focus:shadow-hard-sm" placeholder="At least 8 characters" />
          </div>
          <button type="submit" disabled={busy} data-testid="reset-submit-btn"
            className="w-full border-2 border-black bg-accent py-4 font-display font-bold uppercase tracking-wider shadow-hard brutal-hover disabled:opacity-50">
            {busy ? "Saving…" : "Set new password"}
          </button>
        </form>
      )}
    </div>
  );
}
